import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeProvider, useThemeContext } from '@/contexts/ThemeContext';
import { UserProvider, useUserContext, canAccessView } from '@/contexts/UserContext';
import { SUPPORTED_LANGUAGE_CODES } from '@/i18n/languages';
import { usePlatform } from '@/hooks/usePlatform';
import TeamsSDK from '@/teams/TeamsSDK';
import { GradientBackground } from '@/components/layout/GradientBackground';
import { Sidebar, ViewId } from '@/components/layout/Sidebar';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { UserConfigFAB } from '@/components/common/UserConfigFAB';
import { DemoSimulatorPanel } from '@/demo/DemoSimulatorPanel';
import { DebugPanel } from '@/debug/DebugPanel';
import { SurveyView } from '@/views/SurveyView';
import { SafeView } from '@/views/SafeView';
import { AdminView } from '@/views/AdminView';
import { ExecutiveView } from '@/views/ExecutiveView';
import '@/styles/global.css';

// Product name comes from env so it can be white-labelled without code changes
const APP_NAME = import.meta.env.VITE_APP_NAME ?? 'Sigma Pulse';

function LoadingScreen() {
  return (
    <div className="app-loading">
      <div className="app-loading-spinner" />
      <span>{APP_NAME}</span>
    </div>
  );
}

function AppInner() {
  const { t, i18n } = useTranslation();
  const { platform, teamsContext, isReady } = usePlatform();
  const { setFromTeams } = useThemeContext();
  const { demoUser } = useUserContext();
  const [activeView, setActiveView] = useState<ViewId>(() => {
    try {
      return (localStorage.getItem('pmp_activeView') as ViewId) ?? 'survey';
    } catch {
      return 'survey';
    }
  });

  // Persist active view so F5 restores the current page
  useEffect(() => {
    try { localStorage.setItem('pmp_activeView', activeView); } catch { /* storage unavailable */ }
  }, [activeView]);

  // If the active view becomes inaccessible after a role change, redirect to home
  useEffect(() => {
    if (!canAccessView(activeView, demoUser.role)) {
      setActiveView('survey');
    }
  }, [demoUser.role, activeView]);

  // Teams theme sync — independiente del idioma, corre siempre que haya contexto de Teams.
  useEffect(() => {
    if (teamsContext) {
      setFromTeams(teamsContext.theme);
      TeamsSDK.registerOnThemeChangeHandler(setFromTeams);
    }
  }, [teamsContext, setFromTeams]);

  // Idioma: demoUser.idioma ya trae la precedencia completa resuelta en UserContext
  // (preferredLanguage del perfil > locale de Teams > idioma del navegador > inglés) —
  // este efecto solo la aplica a i18next.
  useEffect(() => {
    if (demoUser.idioma && SUPPORTED_LANGUAGE_CODES.includes(demoUser.idioma) && demoUser.idioma !== i18n.language) {
      void i18n.changeLanguage(demoUser.idioma);
    }
  }, [demoUser.idioma, i18n]);

  if (!isReady) return <LoadingScreen />;

  return (
    <>
      <GradientBackground />

      <Sidebar
        activeView={activeView}
        onViewChange={setActiveView}
      />

      <main className="main-content">
        <div className={`view${activeView === 'survey' ? ' active' : ''}`}>
          <ErrorBoundary><SurveyView onNavigateToGrowth={() => setActiveView('safe')} /></ErrorBoundary>
        </div>
        <div className={`view${activeView === 'safe' ? ' active' : ''}`}>
          <ErrorBoundary><SafeView /></ErrorBoundary>
        </div>
        <div className={`view${activeView === 'admin' ? ' active' : ''}`}>
          <ErrorBoundary><AdminView /></ErrorBoundary>
        </div>
        <div className={`view${activeView === 'executive' ? ' active' : ''}`}>
          <ErrorBoundary><ExecutiveView /></ErrorBoundary>
        </div>
      </main>

      {/* Mobile bottom nav — only accessible views for current role */}
      <nav className="mobile-bottom-nav" aria-label={t('nav.mobileNav')}>
        {(
          [
            { view: 'survey'    as ViewId, icon: 'fa-clipboard-list', labelKey: 'nav.home'      },
            { view: 'safe'      as ViewId, icon: 'fa-seedling',        labelKey: 'nav.growth'    },
            { view: 'admin'     as ViewId, icon: 'fa-cog',             labelKey: 'nav.admin'     },
            { view: 'executive' as ViewId, icon: 'fa-users',           labelKey: 'nav.executive' },
          ] as const
        ).filter(({ view }) => canAccessView(view, demoUser.role))
         .map(({ view, icon, labelKey }) => (
          <button
            key={view}
            className={`mbn-link${activeView === view ? ' active' : ''}`}
            onClick={() => setActiveView(view)}
            aria-label={t(labelKey)}
            aria-current={activeView === view ? 'page' : undefined}
          >
            <i className={`fas ${icon}`} aria-hidden="true" />
            <span>{t(labelKey)}</span>
          </button>
        ))}
      </nav>

      <UserConfigFAB />
      {import.meta.env.VITE_DEMO_MODE && <DemoSimulatorPanel />}
      <DebugPanel />

      {platform === 'teams' && (
        <div className="teams-badge">{t('platform.teams')}</div>
      )}
    </>
  );
}

export function App() {
  return (
    <UserProvider>
      <ThemeProvider>
        <AppInner />
      </ThemeProvider>
    </UserProvider>
  );
}
